/**
 * EconomicsHistoryPage — päeva-tasandi tulu/kulu ajalugu.
 * Kuvab kõik pargid AGGREGATED + per-park breakdown.
 * EconomicsTracker andmete põhjal (energy_economics tabel).
 */

import { useState, useEffect, useMemo } from 'react';

const PERIODS = [
  { id: '7d',  label: '7 päeva' },
  { id: '30d', label: '30 päeva' },
  { id: 'this_week',  label: 'See nädal' },
  { id: 'this_month', label: 'See kuu' },
  { id: 'this_year',  label: 'See aasta' },
  { id: 'custom',     label: 'Vali' },
];

function computeRange(period, customFrom, customTo) {
  const now = Date.now();
  const d = new Date();
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (period === 'custom') {
    if (customFrom && customTo) {
      return { from: new Date(customFrom).getTime(), to: new Date(customTo).getTime() + 86400e3 - 1 };
    }
    return null;
  }
  if (period === '7d') return { from: startOfDay - 7 * 86400e3, to: now };
  if (period === '30d') return { from: startOfDay - 30 * 86400e3, to: now };
  if (period === 'this_week') { const dow = (d.getDay() + 6) % 7; return { from: startOfDay - dow * 86400e3, to: now }; }
  if (period === 'this_month') return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), to: now };
  if (period === 'this_year') return { from: new Date(d.getFullYear(), 0, 1).getTime(), to: now };
  return null;
}

function fmtMoney(n, signed = false) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  const s = v.toFixed(2);
  if (signed) return v >= 0 ? `+${s} €` : `${s} €`;
  return `${s} €`;
}

function fmtKwh(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(2)} MWh`;
  return `${v.toFixed(1)} kWh`;
}

export function EconomicsHistoryPage() {
  const [period, setPeriod] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expandedDate, setExpandedDate] = useState(null);  // mis päev avatud (per-park nägemiseks)

  const range = useMemo(() => computeRange(period, customFrom, customTo), [period, customFrom, customTo]);

  const fetchData = () => {
    if (!range) return;
    setLoading(true);
    setErr(null);
    fetch(`/api/economics/daily?from=${range.from}&to=${range.to}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setData(d))
      .catch(e => { setErr(e.message); setData(null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [period, customFrom, customTo]);

  // Perioodi-kokkuvõte (kogu vahemik)
  const periodTotal = useMemo(() => {
    if (!data?.days) return null;
    const t = {
      revenue_export: 0, cost_import: 0, net_eur: 0, battery_savings: 0,
      solar_kwh: 0, grid_export_kwh: 0, grid_import_kwh: 0,
      battery_charge_kwh: 0, battery_discharge_kwh: 0,
      days_count: data.days.length,
    };
    for (const d of data.days) {
      for (const k of Object.keys(t)) {
        if (k !== 'days_count') t[k] += d.total[k] || 0;
      }
    }
    return t;
  }, [data]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Tulu ajalugu — päevased kokkuvõtted</h1>

      {/* Perioodi valik */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button
            key={p.id} onClick={() => setPeriod(p.id)}
            style={{
              padding: '6px 14px', borderRadius: 4,
              border: '1px solid #ddd',
              background: period === p.id ? '#11093E' : '#fff',
              color: period === p.id ? '#fff' : '#333',
              fontWeight: 500, cursor: 'pointer', fontSize: 13,
            }}
          >{p.label}</button>
        ))}
        {period === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd' }} />
            <span style={{ alignSelf: 'center' }}>—</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd' }} />
          </>
        )}
      </div>

      {/* Perioodi-kokkuvõte */}
      {periodTotal && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24,
          background: '#F9FAF9', padding: 16, borderRadius: 8, border: '1px solid #e0e0e0',
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tulu kokku</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>{fmtMoney(periodTotal.revenue_export, true)}</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>eksport {fmtKwh(periodTotal.grid_export_kwh)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kulu kokku</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>−{(periodTotal.cost_import || 0).toFixed(2)} €</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>import {fmtKwh(periodTotal.grid_import_kwh)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Netokasum</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: (periodTotal.net_eur || 0) >= 0 ? '#15803d' : '#dc2626' }}>{fmtMoney(periodTotal.net_eur, true)}</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>{periodTotal.days_count} päeva · {fmtKwh(periodTotal.solar_kwh)} solar</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aku säästis</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#11093E' }}>{fmtMoney(periodTotal.battery_savings, true)}</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>vs ilma akuta</div>
          </div>
        </div>
      )}

      {err && (
        <div style={{ padding: 12, background: '#ffebee', color: '#c62828', borderRadius: 4, marginBottom: 16 }}>
          Viga: {err}
        </div>
      )}

      {loading && <div style={{ padding: 12, color: '#666' }}>Laeb…</div>}

      {/* Päevade tabel */}
      {data?.days && data.days.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 6, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f5f5f5' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #ccc', width: 110 }}>Kuupäev</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Tulu</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Kulu</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Netokasum</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Aku säästis</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Solar</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Eksport</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Import</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #ccc', width: 90 }}>Pargid</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map(d => (
                <DayRow key={d.date} day={d} expanded={expandedDate === d.date} onToggle={() => setExpandedDate(expandedDate === d.date ? null : d.date)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.days && data.days.length === 0 && !loading && (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Valitud perioodis pole andmeid.</div>
      )}
    </div>
  );
}

function DayRow({ day, expanded, onToggle }) {
  const t = day.total;
  return (
    <>
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 500 }}>{day.date}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#15803d', fontWeight: 500 }}>{fmtMoney(t.revenue_export, true)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#dc2626', fontWeight: 500 }}>−{(t.cost_import || 0).toFixed(2)} €</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: (t.net_eur || 0) >= 0 ? '#15803d' : '#dc2626' }}>{fmtMoney(t.net_eur, true)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#11093E' }}>{fmtMoney(t.battery_savings, true)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>{fmtKwh(t.solar_kwh)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#15803d' }}>{fmtKwh(t.grid_export_kwh)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#dc2626' }}>{fmtKwh(t.grid_import_kwh)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
          <button onClick={onToggle} style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 12 }}>
            {expanded ? '▾ Peida' : `▸ ${day.parks.length}`}
          </button>
        </td>
      </tr>
      {expanded && day.parks.map(p => (
        <tr key={p.slot} style={{ background: '#fafafa', fontSize: 12 }}>
          <td style={{ padding: '6px 12px 6px 32px', color: '#666' }}>{p.slot}. {p.name}</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#15803d' }}>{(p.revenue_export || 0).toFixed(2)} €</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#dc2626' }}>−{(p.cost_import || 0).toFixed(2)} €</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 500, color: (p.net_eur || 0) >= 0 ? '#15803d' : '#dc2626' }}>{fmtMoney(p.net_eur, true)}</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#11093E' }}>{(p.battery_savings || 0).toFixed(2)} €</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>{fmtKwh(p.solar_kwh)}</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#15803d' }}>{fmtKwh(p.grid_export_kwh)}</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#dc2626' }}>{fmtKwh(p.grid_import_kwh)}</td>
          <td></td>
        </tr>
      ))}
    </>
  );
}

export default EconomicsHistoryPage;
